import { useEffect, useRef, useState } from 'react'

import { Tag } from '@vtex/shoreline'

import BadgeIsValid from '~/components/BadgeIsValid'
import Panel from '~/components/Panel'
import { Badge } from '~/components/UI/Badge'
import { Radio } from '~/components/unform'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import {
  CampaignChannel,
  isCampaignChannel
} from '~/modules/retailMedia/constants/campaignChannel'

import { useCampaignForm } from '../../hooks/useCampaignForm'

const CHANNEL_OPTIONS = Object.values(CampaignChannel)

const Channel: React.FC = () => {
  const { t } = useAPPTranslation()

  // Mirrors the selection into CampaignFormContext so sections downstream
  // of this one (media size limit, Meta info panel, review step) can read
  // it reactively, without owning the unform field themselves. Optional
  // chaining: this component also renders outside CampaignFormProvider (see
  // index.spec.tsx/Storybook), where the context falls back to `{}`.
  const { setSelectedChannel: setContextChannel } = useCampaignForm()

  const [selectedChannel, setSelectedChannel] =
    useState<CampaignChannel | null>(null)
  const stepRef = useRef<HTMLDivElement>(null)

  // After picking offsite this block mounts already open. Scroll it into
  // view without focusing a card — the radios are visually hidden, and
  // focusing the wrapper draws a ring on DV360 only.
  useEffect(() => {
    if (selectedChannel) return

    const scrollStepIntoView = () => {
      stepRef.current?.scrollIntoView?.({
        block: 'nearest',
        behavior: 'smooth'
      })
    }

    if (typeof globalThis.requestAnimationFrame !== 'function') {
      scrollStepIntoView()
      return
    }

    const frame = globalThis.requestAnimationFrame(scrollStepIntoView)

    return () => globalThis.cancelAnimationFrame(frame)
  }, [selectedChannel])

  const options = CHANNEL_OPTIONS.map(channel => ({
    value: channel,
    label: t(`rm:campaign.offsite.channel.${channel}`),
    description: t(`rm:campaign.offsite.channel.${channel}.description`),
    sideRight:
      channel === CampaignChannel.META ? (
        <Tag color="yellow" variant="secondary">
          {t('rm:campaign.offsite.channel.meta.accountAccessRequired')}
        </Tag>
      ) : undefined
  }))

  // Mirrors CampaignType/LinkInsertionOrderBlock's "concluded" treatment
  // (tick + description swapping to a green badge with the chosen value)
  // once the operator picks a channel.
  const description = selectedChannel ? (
    <>
      {t('rm:campaign.offsite.channel.selected.message')}{' '}
      <Badge template="successSolid" size="tiny">
        {t(`rm:campaign.offsite.channel.${selectedChannel}`)}
      </Badge>
    </>
  ) : (
    t('rm:campaign.offsite.channel.description')
  )

  return (
    <div ref={stepRef}>
      <Panel
        title={t('rm:campaign.offsite.channel.title')}
        description={description}
        isCollapsible
        initialCollapsibleState={!selectedChannel}
        iconLeft={<BadgeIsValid isValid={!!selectedChannel} />}
      >
        <Radio
          name="channel"
          options={options}
          onChange={event => {
            const value = event.target.value
            if (isCampaignChannel(value)) {
              setSelectedChannel(value)
              setContextChannel?.(value)
            }
          }}
          required
        />
      </Panel>
    </div>
  )
}

export default Channel
